import React, { useEffect, useRef, useState } from "react";
import styles from "./WidgetHeader.module.css";
import {
  ExpandIcon,
  MinimizeIcon,
  CloseIcon,
  PlusIcon,
  BrainIcon,
  HistoryIcon,
} from "./Icons"; // Assuming Icons are in the same common directory
import useAuthStore from "../../store/authStore"; // Import useAuthStore
import useMediaQuery from "../../hooks/useMediaQuery"; // Import useMediaQuery

export interface WidgetHeaderProps {
  chatViewMode: "sidebar" | "fullscreen" | "collapsed"; // Add 'collapsed' for completeness, though header isn't usually shown then
  isHistoryPanelVisibleOnMobile?: boolean; // Optional as it's only relevant in fullscreen
  brandText: string;
  onClose: () => void;
  onExpandOrMinimize: () => void;
  onToggleHistoryPanel?: () => void; // Optional as it's only relevant in fullscreen
  // FP‑1.12 flags
  showHistory?: boolean;
  showNewChat?: boolean;
  showBusinessProfile?: boolean;
  showExpandMinimize?: boolean;
  showClose?: boolean;
  useOverflowOnNarrow?: boolean;
  // Optional actions for new chat and business profile
  onNewChat?: () => void;
  onOpenBusinessProfile?: () => void;
}

const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  chatViewMode,
  isHistoryPanelVisibleOnMobile,
  brandText,
  onClose,
  onExpandOrMinimize,
  onToggleHistoryPanel,
  showHistory = true,
  showNewChat = false,
  showBusinessProfile = false,
  showExpandMinimize = true,
  showClose = true,
  useOverflowOnNarrow = true,
  onNewChat,
  onOpenBusinessProfile,
}) => {
  // Get messageLimits and userStatus from the store
  const messageLimits = useAuthStore((state) => state.messageLimits);
  const userStatus = useAuthStore((state) => state.userStatus);

  // For the new mobile sidebar hamburger menu
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isNarrow = useMediaQuery("(max-width: 359px)");

  // Overflow menu (⋯) state and focus handling
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const overflowButtonRef = useRef<HTMLButtonElement | null>(null);
  const overflowMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOverflowOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOverflowOpen(false);
        overflowButtonRef.current?.focus();
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (
        overflowMenuRef.current &&
        !overflowMenuRef.current.contains(e.target as Node) &&
        !overflowButtonRef.current?.contains(e.target as Node)
      ) {
        setIsOverflowOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOverflowOpen]);
  // We'll use the passed 'chatViewMode' prop and 'onToggleHistoryPanel' prop.
  // 'isHistoryPanelVisibleOnMobile' prop is used for the title/aria-label of the existing fullscreen button.

  // Calculate remaining messages with more robust null checks
  const remainingMessages =
    messageLimits &&
    typeof messageLimits.max === "number" &&
    typeof messageLimits.current === "number"
      ? Math.max(0, messageLimits.max - messageLimits.current)
      : null;

  // Only display message limits if user status is known and we have valid messageLimits data
  const displayMessageLimits =
    userStatus !== "unknown" &&
    remainingMessages !== null &&
    messageLimits?.max !== null;

  return (
    <header className={styles.header}>
      {/* History toggle (hamburger) for Sidebar on Mobile */}
      {showHistory && isMobile && chatViewMode === "sidebar" && onToggleHistoryPanel && (
        <button
          onClick={onToggleHistoryPanel}
          className={`${styles.iconButton} ${styles.menuButton}`} // Reuse existing styles
          title={
            isHistoryPanelVisibleOnMobile
              ? "Ascunde istoricul conversațiilor"
              : "Arată istoricul conversațiilor"
          }
          aria-label={
            isHistoryPanelVisibleOnMobile
              ? "Ascunde panoul istoricului conversațiilor"
              : "Arată panoul istoricului conversațiilor"
          }
        >
          <HistoryIcon className={styles.icon} title="Istoric conversații" />
        </button>
      )}

      {/* History toggle for Fullscreen (any device) */}
      {showHistory && chatViewMode === "fullscreen" && onToggleHistoryPanel && (
        <button
          onClick={onToggleHistoryPanel}
          className={`${styles.iconButton} ${styles.menuButton}`}
          title={
            isHistoryPanelVisibleOnMobile
              ? "Ascunde istoricul conversațiilor"
              : "Arată istoricul conversațiilor"
          }
          aria-label={
            isHistoryPanelVisibleOnMobile
              ? "Ascunde panoul istoricului conversațiilor"
              : "Arată panoul istoricului conversațiilor"
          }
        >
          <HistoryIcon className={styles.icon} title="Istoric conversații" />
        </button>
      )}
      <div className={styles.brandContainer}>
        <span className={styles.brand}>{brandText}</span>
        {displayMessageLimits && messageLimits && (
          <span className={styles.messageLimits}>
            {remainingMessages}/{messageLimits.max} mesaje rămase
          </span>
        )}
      </div>
      <div className={styles.iconButtonContainer}>
        {/* Optional direct actions (hidden on very narrow if overflow is enabled) */}
        {showNewChat && onNewChat && !(useOverflowOnNarrow && isNarrow) && (
          <button
            onClick={onNewChat}
            className={styles.iconButton}
            title="Conversație nouă"
            aria-label="Începe o conversație nouă"
          >
            <PlusIcon className={styles.icon} title="Pictogramă conversație nouă" />
          </button>
        )}
        {showBusinessProfile && onOpenBusinessProfile && !(useOverflowOnNarrow && isNarrow) && (
          <button
            onClick={onOpenBusinessProfile}
            className={styles.iconButton}
            title="Profil afacere"
            aria-label="Deschide profilul de afacere"
          >
            <BrainIcon className={styles.icon} title="Pictogramă informații mindspace" />
          </button>
        )}

        {/* Overflow (⋯) consolidates New Chat + Profile on very narrow screens */}
        {useOverflowOnNarrow && isNarrow && (showNewChat || showBusinessProfile) && (
          <div className={styles.overflowWrapper}>
            <button
              ref={overflowButtonRef}
              className={`${styles.iconButton} ${styles.overflowButton}`}
              onClick={() => setIsOverflowOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={isOverflowOpen}
              aria-controls="widget-header-overflow-menu"
              title="Mai multe acțiuni"
              aria-label="Deschide meniul cu acțiuni"
            >
              <span aria-hidden>⋯</span>
            </button>
            {isOverflowOpen && (
              <div
                id="widget-header-overflow-menu"
                ref={overflowMenuRef}
                role="menu"
                className={styles.overflowMenu}
              >
                {showNewChat && onNewChat && (
                  <button
                    role="menuitem"
                    className={styles.overflowMenuItem}
                    onClick={() => {
                      setIsOverflowOpen(false);
                      onNewChat();
                    }}
                    title="Conversație nouă"
                    aria-label="Începe o conversație nouă"
                  >
                    <PlusIcon className={styles.menuIcon} title="Pictogramă conversație nouă" />
                    <span>Chat nou</span>
                  </button>
                )}
                {showBusinessProfile && onOpenBusinessProfile && (
                  <button
                    role="menuitem"
                    className={styles.overflowMenuItem}
                    onClick={() => {
                      setIsOverflowOpen(false);
                      onOpenBusinessProfile();
                    }}
                    title="Profil afacere"
                    aria-label="Deschide profilul de afacere"
                  >
                    <BrainIcon className={styles.menuIcon} title="Pictogramă informații mindspace" />
                    <span>Profil afacere</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Expand / Minimize */}
        {showExpandMinimize && (chatViewMode === "sidebar" || chatViewMode === "fullscreen") && (
          <button
            onClick={onExpandOrMinimize}
            className={styles.iconButton}
            title={
              chatViewMode === "fullscreen"
                ? "Minimizează chat"
                : "Extinde la ecran complet"
            }
            aria-label={
              chatViewMode === "fullscreen"
                ? "Minimizează chat"
                : "Extinde la vizualizarea pe ecran complet"
            }
          >
            {chatViewMode === "fullscreen" ? (
              <MinimizeIcon
                className={styles.icon}
                title="Pictogramă minimizare"
              />
            ) : (
              <ExpandIcon
                className={styles.icon}
                title="Pictogramă extindere"
              />
            )}
          </button>
        )}
        {/* Close */}
        {showClose && (
          <button
            onClick={onClose}
            className={styles.iconButton}
            title="Închide chat"
            aria-label="Închide chat"
          >
            <CloseIcon className={styles.icon} title="Pictogramă închidere" />
          </button>
        )}
      </div>
    </header>
  );
};

export default WidgetHeader;
